import {
  CognitoRefreshToken,
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
} from "amazon-cognito-identity-js";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { config } from "../assets/config";
import {
  getIdToken,
  getRefreshToken,
  saveAuthToken as storeAuthTokens,
  clearAuthToken as clearAuthTokens,
} from "./authTokenStorage";

function saveAuthToken(session: CognitoUserSession) {
  const idToken = session.getIdToken().getJwtToken();
  const accessToken = session.getAccessToken().getJwtToken();
  const refreshToken = session.getRefreshToken().getToken();

  storeAuthTokens({ idToken, accessToken, refreshToken });
}
function deleteAuthToken() {
  clearAuthTokens();
}

export const isAuthenticated = (): boolean => {
  return getIdToken() !== null;
};

// idTokenの有効期限切れをHTTPステータス（401）のみで検知すると、
// レスポンスストリーミング対応のLambda Function URL等では
// トークン期限切れがステータス200で返ってしまうケースがあり検知できないため、
// 呼び出し前にクライアント側でexpクレームを確認し、期限切れ/間近であれば能動的にリフレッシュする。
const ID_TOKEN_EXPIRY_BUFFER_SECONDS = 60;

/**
 * 有効なidTokenを返す。有効期限切れ、または期限間近（残り60秒未満）の場合は
 * 呼び出し前にrefreshTokenWithLock()でトークンを更新してから返す。
 * @returns 有効なidToken
 * @throws ログイン状態でない、またはリフレッシュに失敗した場合
 */
export const ensureValidIdToken = async (): Promise<string> => {
  const idToken = getIdToken();
  if (!idToken) {
    throw new Error("User is not logged in or idToken is missing");
  }

  try {
    const decoded = jwtDecode<JwtPayload>(idToken);
    const nowInSeconds = Date.now() / 1000;
    if (decoded.exp !== undefined && decoded.exp - nowInSeconds > ID_TOKEN_EXPIRY_BUFFER_SECONDS) {
      // 有効期限に十分な余裕があるためそのまま利用する
      return idToken;
    }
  } catch (error) {
    // デコードに失敗した場合は安全側に倒し、リフレッシュを試みる
    console.error("Failed to decode idToken for expiry check:", error);
  }

  const session = await refreshTokenWithLock();
  return session.getIdToken().getJwtToken();
};

export type AuthUserInfo = {
  email: string;
  sub: string;
  username: string;
};

export const getAuthInfoForWebStorage = (): (JwtPayload & { email?: string; 'cognito:username'?: string }) | null => {
  const idToken = getIdToken();
  if (idToken) {
    try {
      const decoded = jwtDecode<JwtPayload & { email?: string; 'cognito:username'?: string }>(idToken);
      return decoded || null;
    } catch (error) {
      console.error("Failed to decode token:", error);
      return null;
    }
  }
  return null;
};

// PKCE/OAuth state を保存する sessionStorage のキー
const PKCE_VERIFIER_KEY = 'ceas.oauth.pkce_verifier';
const OAUTH_STATE_KEY = 'ceas.oauth.state';

const toBase64Url = (buffer: ArrayBuffer | Uint8Array): string => {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return toBase64Url(array);
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return toBase64Url(hash);
};

// Pre Sign-up Lambda がサインアップ拒否時に throw する文言の一部
export const ACCOUNT_NOT_REGISTERED_MESSAGE_KEYWORD = 'ACCOUNT_NOT_REGISTERED';

/**
 * Google SSOによるサインインを開始する。
 * PKCEフローで Cognito の /oauth2/authorize に identity_provider=Google を指定してリダイレクト。
 */
export const signInWithGoogle = async (): Promise<void> => {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = generateCodeVerifier();

  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(OAUTH_STATE_KEY, state);

  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    redirect_uri: config.cognito.oauth.redirectSignIn,
    identity_provider: 'Google',
    scope: 'email openid profile',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  window.location.href = `https://${config.cognito.oauth.domain}/oauth2/authorize?${params.toString()}`;
};

/**
 * OAuth コールバックで受け取った認可コードをCognitoの/oauth2/tokenエンドポイントと交換する。
 * 取得したトークンは既存パスワード認証と同じ localStorage 構造で保存する。
 */
export const exchangeCodeForTokens = async (
  code: string,
  state: string
): Promise<AuthUserInfo> => {
  const savedState = sessionStorage.getItem(OAUTH_STATE_KEY);
  if (!savedState || state !== savedState) {
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    throw new Error('OAuth state mismatch');
  }

  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  if (!verifier) {
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    throw new Error('PKCE verifier not found');
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code,
    redirect_uri: config.cognito.oauth.redirectSignIn,
    code_verifier: verifier,
  });

  const response = await fetch(`https://${config.cognito.oauth.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);

  if (!response.ok) {
    throw new Error(`Token exchange failed: ${response.status} ${response.statusText}`);
  }

  const tokens: { id_token: string; access_token: string; refresh_token: string; expires_in: number } =
    await response.json();

  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: tokens.id_token }),
    AccessToken: new CognitoAccessToken({ AccessToken: tokens.access_token }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refresh_token }),
  });
  saveAuthToken(session);

  const payload = jwtDecode<JwtPayload & Partial<{ email: string; 'cognito:username': string }>>(tokens.id_token);
  if (!payload.sub || !payload.email || !payload['cognito:username']) {
    throw new Error('Invalid ID token payload: required claims are missing');
  }
  return {
    email: payload.email,
    sub: payload.sub,
    username: payload['cognito:username'],
  };
};

// トークンリフレッシュの排他制御（race condition防止）
let refreshPromise: Promise<CognitoUserSession> | null = null;

export const refreshTokenWithLock = (): Promise<CognitoUserSession> => {
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = refreshToken().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
};

// SSOユーザー（PKCEフロー）向けのトークンリフレッシュ
// Cognitoの仕様上、refresh_token grantのレスポンスには新しいrefresh_tokenが含まれないため既存値を流用する
const refreshSsoToken = async (refreshTokenValue: string): Promise<CognitoUserSession> => {
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: config.clientId,
    refresh_token: refreshTokenValue,
  });

  const response = await fetch(`https://${config.cognito.oauth.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    deleteAuthToken();
    throw new Error(`Failed to refresh SSO token: ${response.status} ${response.statusText}`);
  }

  const tokens: { id_token: string; access_token: string } = await response.json();

  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: tokens.id_token }),
    AccessToken: new CognitoAccessToken({ AccessToken: tokens.access_token }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: refreshTokenValue }),
  });
  saveAuthToken(session);
  return session;
};

export const refreshToken = async (): Promise<CognitoUserSession> => {
  const refreshTokenValue = getRefreshToken();
  if (!refreshTokenValue) {
    throw new Error("User is not logged in or refresh token is missing");
  }
  return refreshSsoToken(refreshTokenValue);
};

export const signOut = (): Promise<void> => {
  deleteAuthToken();
  return Promise.resolve();
};
